import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getDocuments, getDocumentTypes, deleteDocument } from '@/lib/api';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Search, Eye, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, getExpiryStatusClass, getExpiryStatusLabel } from '@/lib/utils';

export default function DocumentsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialExpiring = searchParams.get('expiring');
  
  const [documents, setDocuments] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [filterDocType, setFilterDocType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [expiringDays, setExpiringDays] = useState(initialExpiring || '');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);

  useEffect(() => {
    loadDocTypes();
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [filterDocType, filterStatus, expiringDays]);

  const loadDocTypes = async () => {
    try {
      const res = await getDocumentTypes();
      setDocTypes(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filterDocType) params.doc_type_id = filterDocType;
      if (filterStatus) params.status = filterStatus;
      if (expiringDays) params.expiring_in_days = parseInt(expiringDays);
      
      const res = await getDocuments(params);
      setDocuments(res.data.items);
      setTotal(res.data.total);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!docToDelete) return;
    try {
      await deleteDocument(docToDelete.id);
      toast.success('Document deleted');
      loadDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    } finally {
      setDeleteDialogOpen(false);
      setDocToDelete(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Documents</h1>
          <p className="text-muted-foreground">Track document expiry dates</p>
        </div>
        <Button onClick={() => navigate('/documents/new')} data-testid="new-document-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Document
        </Button>
      </div>

      {/* Expiry Quick Filters */}
      <div className="flex gap-2 flex-wrap">
        <Button 
          variant={expiringDays === '7' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setExpiringDays(expiringDays === '7' ? '' : '7')}
          className={expiringDays === '7' ? '' : 'status-critical'}
        >
          Expiring in 7 Days
        </Button>
        <Button 
          variant={expiringDays === '30' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setExpiringDays(expiringDays === '30' ? '' : '30')}
          className={expiringDays === '30' ? '' : 'status-warning'}
        >
          Expiring in 30 Days
        </Button>
        <Button 
          variant={expiringDays === '60' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setExpiringDays(expiringDays === '60' ? '' : '60')}
          className={expiringDays === '60' ? '' : 'status-upcoming'}
        >
          Expiring in 60 Days
        </Button>
        <Button 
          variant={filterStatus === 'Expired' ? 'destructive' : 'outline'} 
          size="sm"
          onClick={() => setFilterStatus(filterStatus === 'Expired' ? '' : 'Expired')}
        >
          Expired
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-wrap gap-4">
            <Select value={filterDocType} onValueChange={(v) => setFilterDocType(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[180px]" data-testid="filter-doc-type">
                <SelectValue placeholder="All Doc Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Doc Types</SelectItem>
                {docTypes.map((dt) => (
                  <SelectItem key={dt.id} value={dt.id}>{dt.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Document Type</TableHead>
                    <TableHead>Doc Number</TableHead>
                    <TableHead>Issue Date</TableHead>
                    <TableHead>Expiry Date</TableHead>
                    <TableHead>Days Left</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.map((doc) => (
                    <TableRow key={doc.id} data-testid={`doc-row-${doc.id}`}>
                      <TableCell className="font-mono text-xs">{doc.document_code}</TableCell>
                      <TableCell className="font-medium">{doc.owner_name}</TableCell>
                      <TableCell>{doc.doc_type?.name || '-'}</TableCell>
                      <TableCell className="font-mono text-sm">{doc.doc_number || '-'}</TableCell>
                      <TableCell className="text-muted-foreground">{formatDate(doc.issue_date)}</TableCell>
                      <TableCell>{formatDate(doc.expiry_date)}</TableCell>
                      <TableCell>
                        <Badge className={getExpiryStatusClass(doc.days_until_expiry)}>
                          {doc.days_until_expiry !== null ? `${doc.days_until_expiry}d` : '-'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={getExpiryStatusClass(doc.days_until_expiry)}>
                          {doc.expiry_status || getExpiryStatusLabel(doc.days_until_expiry)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/documents/${doc.id}`)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setDocToDelete(doc);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <div className="mt-4 text-sm text-muted-foreground">
            Showing {documents.length} of {total} documents
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this document? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
